/**
 * Category form hook: list/create/edit/delete product categories.
 * @typedef {import('../../../models/ProductCategory').ProductCategory} ProductCategory
 * @typedef {import('../../../models/ProductCategory').ProductCategoryCreate} ProductCategoryCreate
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getProductCategories,
  createProductCategory,
  updateProductCategory,
  deleteProductCategory
} from '../../../services/productCategoryService';
import { searchMlbCategories, getMlbCategoryChildren } from '../../../services/mlbCategoriesService';
import { useClickOutside, TrashIcon, LIST_STALE_TIME_MS, distinctByKey, createHandleChange, clearFormAlerts } from '../../common';
import {
  CATEGORIES_QUERY_KEY,
  emptyFormData,
  categoryToFormData
} from './categoryFormUtils';
import { useAppStore } from '../../../store/appStore';

export { TrashIcon };

export function useCategoryForm() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const categoryForm = useAppStore((s) => s.categoryForm);
  const setCategoryForm = useAppStore((s) => s.setCategoryForm);
  const setInsertBasicData = useAppStore((s) => s.setInsertBasicData);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedCategoryId,
    selectedCategory,
    showCategoryDropdown,
    showSubCategoryDropdown,
    categoryFieldTouched,
    showMlbCategoryDropdown,
    showMlbChildDropdown
  } = categoryForm;

  const setMode = React.useCallback((v) => setCategoryForm((prev) => ({ ...prev, mode: v })), [setCategoryForm]);
  const setFormData = React.useCallback(
    (v) =>
      setCategoryForm((prev) => ({
        ...prev,
        formData: typeof v === 'function' ? v(prev.formData) : v
      })),
    [setCategoryForm]
  );
  const setFormError = React.useCallback((v) => setCategoryForm((prev) => ({ ...prev, formError: v })), [setCategoryForm]);
  const setFormSuccess = React.useCallback((v) => setCategoryForm((prev) => ({ ...prev, formSuccess: v })), [setCategoryForm]);
  const setSubmitting = React.useCallback((v) => setCategoryForm((prev) => ({ ...prev, submitting: v })), [setCategoryForm]);
  const setDeleting = React.useCallback((v) => setCategoryForm((prev) => ({ ...prev, deleting: v })), [setCategoryForm]);
  const setSelectedCategoryId = React.useCallback(
    (v) => setCategoryForm((prev) => ({ ...prev, selectedCategoryId: v })),
    [setCategoryForm]
  );
  const setSelectedCategory = React.useCallback(
    (v) => setCategoryForm((prev) => ({ ...prev, selectedCategory: v })),
    [setCategoryForm]
  );
  const setShowCategoryDropdown = React.useCallback(
    (v) => setCategoryForm((prev) => ({ ...prev, showCategoryDropdown: v })),
    [setCategoryForm]
  );
  const setShowSubCategoryDropdown = React.useCallback(
    (v) => setCategoryForm((prev) => ({ ...prev, showSubCategoryDropdown: v })),
    [setCategoryForm]
  );
  const setCategoryFieldTouched = React.useCallback(
    (v) => setCategoryForm((prev) => ({ ...prev, categoryFieldTouched: v })),
    [setCategoryForm]
  );
  const setShowMlbCategoryDropdown = React.useCallback(
    (v) => setCategoryForm((prev) => ({ ...prev, showMlbCategoryDropdown: v })),
    [setCategoryForm]
  );
  const setShowMlbChildDropdown = React.useCallback(
    (v) => setCategoryForm((prev) => ({ ...prev, showMlbChildDropdown: v })),
    [setCategoryForm]
  );

  const categoryDropdownRef = React.useRef(null);
  const subCategoryDropdownRef = React.useRef(null);
  const mlbCategoryDropdownRef = React.useRef(null);
  const mlbChildDropdownRef = React.useRef(null);

  const { data: categoriesResponse, isLoading: categoriesLoading } = useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: () => getProductCategories(token),
    enabled: !!token && mode === 'edit' && categoryFieldTouched,
    staleTime: LIST_STALE_TIME_MS
  });

  const categoryNameForMlb = formData.category_name?.trim() ?? '';
  const mlbCategoryId = formData.mlb_category_id?.trim() || null;

  const { data: mlbCategoriesResponse, isLoading: mlbCategoriesLoading } = useQuery({
    queryKey: ['mlb-categories', categoryNameForMlb],
    queryFn: () => searchMlbCategories(token, categoryNameForMlb, 50),
    enabled: !!token && showMlbCategoryDropdown && !!categoryNameForMlb,
    staleTime: 5 * 60 * 1000
  });

  const { data: mlbChildrenResponse, isLoading: mlbChildrenLoading } = useQuery({
    queryKey: ['mlb-category-children', mlbCategoryId],
    queryFn: () => getMlbCategoryChildren(token, formData.mlb_category_id, undefined, 50),
    enabled: !!token && !!mlbCategoryId,
    staleTime: 5 * 60 * 1000
  });

  const sectionLoading = categoriesLoading || mlbCategoriesLoading || mlbChildrenLoading;
  React.useEffect(() => {
    setInsertBasicData((prev) => ({
      ...prev,
      sectionLoading: { ...prev.sectionLoading, categories: sectionLoading }
    }));
  }, [sectionLoading, setInsertBasicData]);

  const categories = categoriesResponse?.product_categories ?? [];
  const distinctCategories = React.useMemo(() => distinctByKey(categories, 'id'), [categories]);
  const subCategoryOptions = React.useMemo(
    () => (selectedCategoryId != null ? categories.filter((r) => r.id === selectedCategoryId) : []),
    [categories, selectedCategoryId]
  );
  const mlbCategoryOptions = mlbCategoriesResponse?.categories ?? [];
  const mlbChildOptions = mlbChildrenResponse?.children ?? [];

  useClickOutside(
    [categoryDropdownRef, subCategoryDropdownRef, mlbCategoryDropdownRef, mlbChildDropdownRef],
    () => {
      setShowCategoryDropdown(false);
      setShowSubCategoryDropdown(false);
      setShowMlbCategoryDropdown(false);
      setShowMlbChildDropdown(false);
    }
  );

  const handleChange = React.useMemo(
    () => createHandleChange(setFormData, setFormError, setFormSuccess),
    [setFormData, setFormError, setFormSuccess]
  );

  const handleModeChange = (newMode) => {
    setMode(newMode);
    clearFormAlerts(setFormError, setFormSuccess);
    setFormData(emptyFormData());
    setSelectedCategoryId(null);
    setSelectedCategory(null);
    setShowCategoryDropdown(false);
    setShowSubCategoryDropdown(false);
    setShowMlbCategoryDropdown(false);
    setShowMlbChildDropdown(false);
    if (newMode === 'create') setCategoryFieldTouched(false);
  };

  const handleSelectCategoryRow = (row) => {
    setSelectedCategoryId(row.id);
    setSelectedCategory(null);
    setFormData({
      ...emptyFormData(),
      category_name: row.category_name?.trim() ?? '',
      sub_category_name: ''
    });
    setShowCategoryDropdown(false);
    setShowSubCategoryDropdown(false);
    setShowMlbCategoryDropdown(false);
    setShowMlbChildDropdown(false);
    clearFormAlerts(setFormError, setFormSuccess);
  };

  /** @param {ProductCategory} row */
  const handleSelectSubCategoryRow = (row) => {
    setSelectedCategory(row);
    setFormData(categoryToFormData(row));
    setShowSubCategoryDropdown(false);
    setShowMlbCategoryDropdown(false);
    setShowMlbChildDropdown(false);
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const handleSelectMlbCategory = (item) => {
    setFormData((prev) => ({
      ...prev,
      mlb_category_id: item.category_id ?? '',
      mlb_category_child_id: '',
      mlbCategoryDisplayName: item.category_name?.trim() || item.category_id || '',
      mlbCategoryChildDisplayName: ''
    }));
    setShowMlbCategoryDropdown(false);
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const handleSelectMlbChild = (item) => {
    setFormData((prev) => ({
      ...prev,
      mlb_category_child_id: item.child_id ?? '',
      mlbCategoryChildDisplayName: item.child_name?.trim() || item.child_id || ''
    }));
    setShowMlbChildDropdown(false);
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const clearMlbCategory = () => {
    setFormData((prev) => ({
      ...prev,
      mlb_category_id: '',
      mlb_category_child_id: '',
      mlbCategoryDisplayName: '',
      mlbCategoryChildDisplayName: ''
    }));
    setShowMlbCategoryDropdown(false);
    setShowMlbChildDropdown(false);
  };

  const clearMlbChild = () => {
    setFormData((prev) => ({
      ...prev,
      mlb_category_child_id: '',
      mlbCategoryChildDisplayName: ''
    }));
    setShowMlbChildDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearFormAlerts(setFormError, setFormSuccess);
    setSubmitting(true);

    try {
      const payload = {
        category_name: formData.category_name?.trim() || null,
        sub_category_name: formData.sub_category_name?.trim() || null,
        mlb_category_id: formData.mlb_category_id?.trim() || null,
        mlb_category_child_id: formData.mlb_category_child_id?.trim() || null
      };

      if (mode === 'create') {
        const result = await createProductCategory(token, payload);
        if (result.success) {
          setFormSuccess('Category created successfully!');
          setFormData(emptyFormData());
          queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
        } else {
          setFormError(result.error || 'Failed to create category');
        }
      } else {
        if (!selectedCategory || selectedCategory.id == null || selectedCategory.sub_category_id == null) {
          setFormError('Please select a category and sub-category to edit');
          setSubmitting(false);
          return;
        }
        const result = await updateProductCategory(
          token,
          selectedCategory.id,
          selectedCategory.sub_category_id,
          payload
        );
        if (result.success) {
          setFormSuccess('Changes saved successfully!');
          setFormData(emptyFormData());
          setSelectedCategoryId(null);
          setSelectedCategory(null);
          queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
        } else {
          setFormError(result.error || 'Failed to update category');
        }
      }
    } catch (error) {
      setFormError(error.message || (mode === 'create' ? 'Failed to create category' : 'Failed to update category'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedCategory?.id == null || selectedCategory?.sub_category_id == null) return;
    clearFormAlerts(setFormError, setFormSuccess);
    setDeleting(true);
    try {
      const result = await deleteProductCategory(
        token,
        selectedCategory.id,
        selectedCategory.sub_category_id
      );
      if (result.success) {
        setFormSuccess('Category deleted.');
        setFormData(emptyFormData());
        setSelectedCategoryId(null);
        setSelectedCategory(null);
        setShowCategoryDropdown(false);
        setShowSubCategoryDropdown(false);
        queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });
      } else {
        setFormError(result.error || 'Failed to delete category');
      }
    } catch (error) {
      setFormError(error.message || 'Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  const isEditMode = mode === 'edit';
  const mlbCategoryFieldEnabled = !!categoryNameForMlb;
  const mlbChildFieldEnabled = !!mlbCategoryId;

  return {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedCategoryId,
    selectedCategory,
    categoriesLoading,
    distinctCategories,
    subCategoryOptions,
    categoryDropdownRef,
    subCategoryDropdownRef,
    showCategoryDropdown,
    showSubCategoryDropdown,
    handleModeChange,
    handleChange,
    handleSelectCategoryRow,
    handleSelectSubCategoryRow,
    handleSubmit,
    handleDelete,
    isEditMode,
    setShowCategoryDropdown,
    setShowSubCategoryDropdown,
    setCategoryFieldTouched,
    mlbCategoryFieldEnabled,
    mlbChildFieldEnabled,
    showMlbCategoryDropdown,
    setShowMlbCategoryDropdown,
    showMlbChildDropdown,
    setShowMlbChildDropdown,
    mlbCategoryDropdownRef,
    mlbChildDropdownRef,
    mlbCategoryOptions,
    mlbChildOptions,
    mlbCategoriesLoading,
    mlbChildrenLoading,
    handleSelectMlbCategory,
    handleSelectMlbChild,
    clearMlbCategory,
    clearMlbChild
  };
}
